import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import IDCard from "@/components/IDCard";
import { X, LayoutTemplate, Check, Loader2, Users, Search } from "lucide-react";

const DEFAULT_KEY = ""; // "" = Default (CMS) design (stored as null)

/**
 * Templates picker — choose ONE ID-card template and apply it ONLY to the
 * members you tick (POST /api/idcard-templates-apply). Members you don't tick
 * keep their current template; members on Default keep the Default (CMS) design.
 * The "Currently using" filter makes it easy to pick e.g. everyone on Student.
 */
export default function ApplyTemplateAllModal({ previewUser, onClose, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [checked, setChecked] = useState(() => new Set(previewUser?.id ? [previewUser.id] : []));
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [clearOverrides, setClearOverrides] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const loadSeq = useRef(0);
  const loadMembers = () => {
    const seq = ++loadSeq.current;
    return api.get("/users")
      .then((r) => { if (seq === loadSeq.current) setMembers(r.data || []); })
      .catch((e) => { if (seq === loadSeq.current) setError(formatApiError(e)); });
  };

  useEffect(() => {
    api.get("/idcard-templates").then((r) => setTemplates(r.data || [])).catch((e) => setError(formatApiError(e)));
    loadMembers();
  }, []);

  const options = useMemo(
    () => [{ key: DEFAULT_KEY, label: "Default (CMS)", description: "The dojo's main ID-card design.", config: {} }, ...templates],
    [templates],
  );
  const labelFor = (k) => options.find((o) => o.key === (k || DEFAULT_KEY))?.label || k || "Default (CMS)";
  const selectedTpl = options.find((o) => o.key === selectedKey);

  const counts = useMemo(() => {
    const c = {};
    members.forEach((m) => { const k = m.idcard_template || DEFAULT_KEY; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [members]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((m) => {
      if (filter !== "all" && (m.idcard_template || DEFAULT_KEY) !== filter) return false;
      if (!q) return true;
      return [m.name, m.email, m.username, m.member_number].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [members, filter, query]);

  // Changing the "Currently using" filter unticks members it hides, so nobody
  // the admin can't see gets changed by accident.
  const changeFilter = (value) => {
    setFilter(value);
    if (value === "all") return;
    setChecked((prev) => new Set([...prev].filter((id) => {
      const m = members.find((x) => x.id === id);
      return m && (m.idcard_template || DEFAULT_KEY) === value;
    })));
  };
  const hiddenChecked = [...checked].filter((id) => !shown.some((m) => m.id === id)).length;

  const allShownChecked = shown.length > 0 && shown.every((m) => checked.has(m.id));
  const toggle = (id) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleShown = () => setChecked((prev) => {
    const n = new Set(prev);
    if (allShownChecked) shown.forEach((m) => n.delete(m.id));
    else shown.forEach((m) => n.add(m.id));
    return n;
  });

  const apply = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/idcard-templates-apply", {
        key: selectedKey || null,
        user_ids: Array.from(checked),
        clear_overrides: clearOverrides,
      });
      setDone(data);
      setConfirming(false);
      if (onApplied) onApplied({ ...data, user_ids: Array.from(checked) });
      loadMembers();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const canApply = selectedKey !== null && checked.size > 0 && !busy;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="apply-template-modal">
      <div className="bg-[var(--dojo-card)] border border-[var(--dojo-border)] w-full max-w-5xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--dojo-border)]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">ID Card Templates</div>
            <h3 className="font-serif text-2xl">Apply a template to selected members</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--dojo-paper-alt)]" data-testid="apply-template-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-4" data-testid="apply-template-success">
            <div className="mx-auto w-12 h-12 rounded-full bg-[var(--dojo-green)] text-white flex items-center justify-center">
              <Check className="w-6 h-6" />
            </div>
            <p className="font-serif text-xl">
              “{labelFor(done.key)}” is now on {done.updated} selected member ID card{done.updated === 1 ? "" : "s"}.
            </p>
            <p className="text-sm text-[var(--dojo-ink-soft)]">
              Everyone else kept their current template.
              {done.cleared_overrides ? " Personal card tweaks were cleared for the selected members." : ""}
            </p>
            <div className="flex justify-center gap-2">
              <button onClick={() => { setDone(null); setChecked(new Set()); }} className="btn-outline" data-testid="apply-template-again">Apply another</button>
              <button onClick={onClose} className="btn-primary" data-testid="apply-template-done">Done</button>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6 p-6">
            {/* 1. Template */}
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">1 · Choose a template</div>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="apply-template-options">
                {options.map((t) => {
                  const cfg = t.config || {};
                  const isSel = selectedKey === t.key;
                  return (
                    <button
                      key={t.key || "__default"}
                      type="button"
                      onClick={() => { setSelectedKey(t.key); setConfirming(false); }}
                      className={`text-left border p-3 transition-colors ${isSel ? "border-[var(--dojo-green)] ring-2 ring-[var(--dojo-green)]" : "border-[var(--dojo-border)] hover:border-[var(--dojo-ink-soft)]"}`}
                      data-testid={`apply-template-option-${t.key || "default"}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 shrink-0 border border-black/10" style={{ background: cfg.accent_color || "#D7263D" }} />
                        <span className="font-medium text-sm flex-1 min-w-0 break-words">{t.label}</span>
                        {isSel && <Check className="w-4 h-4 shrink-0 text-[var(--dojo-green)]" />}
                      </div>
                      <div className="text-[11px] text-[var(--dojo-ink-soft)] mt-1">{counts[t.key] || 0} member{(counts[t.key] || 0) === 1 ? "" : "s"} use it now</div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] flex items-center gap-2 pt-2">
                <LayoutTemplate className="w-3.5 h-3.5" /> Preview
              </div>
              {selectedKey !== null && previewUser ? (
                <div data-testid="apply-template-preview">
                  <IDCard
                    user={{ ...previewUser, idcard_template: selectedKey || null, idcard_overrides: clearOverrides ? {} : previewUser.idcard_overrides }}
                    defaultOrientation="horizontal"
                  />
                </div>
              ) : (
                <div className="border border-dashed border-[var(--dojo-border)] p-6 text-center text-sm text-[var(--dojo-ink-soft)]">
                  Select a template to preview it.
                </div>
              )}
            </div>

            {/* 2. Members */}
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">2 · Choose the members</div>
              <div className="flex gap-2">
                <select className="input flex-1" value={filter} onChange={(e) => changeFilter(e.target.value)} data-testid="apply-template-filter">
                  <option value="all">Currently using: any template ({members.length})</option>
                  {options.map((o) => (
                    <option key={o.key || "__default"} value={o.key}>Currently using: {o.label} ({counts[o.key] || 0})</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--dojo-ink-soft)]" />
                <input className="input" style={{ paddingLeft: "2.25rem" }} placeholder="Search name, email or member #" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="apply-template-search" />
              </div>
              <div className="flex items-center justify-between text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={allShownChecked} onChange={toggleShown} data-testid="apply-template-select-shown" />
                  Select all shown ({shown.length})
                </label>
                <span className="text-[var(--dojo-ink-soft)]" data-testid="apply-template-selected-count">{checked.size} selected{hiddenChecked ? ` · ${hiddenChecked} not shown` : ""}</span>
              </div>
              <div className="border border-[var(--dojo-border)] max-h-[300px] overflow-y-auto divide-y divide-[var(--dojo-border)]" data-testid="apply-template-members">
                {shown.length === 0 && <div className="p-4 text-sm text-[var(--dojo-ink-soft)]">No members match.</div>}
                {shown.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--dojo-paper-alt)]" data-testid={`apply-template-member-${m.id}`}>
                    <input type="checkbox" checked={checked.has(m.id)} onChange={() => toggle(m.id)} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm truncate">{m.name}</span>
                      <span className="block text-[11px] text-[var(--dojo-ink-soft)] truncate">{m.email || m.username || m.member_number} · {m.role}</span>
                    </span>
                    <span className="shrink-0 text-[10px] uppercase tracking-[0.14em] border border-[var(--dojo-border)] px-2 py-0.5">{labelFor(m.idcard_template)}</span>
                  </label>
                ))}
              </div>

              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={clearOverrides} onChange={(e) => setClearOverrides(e.target.checked)} className="mt-1" data-testid="apply-template-clear-overrides" />
                <span>
                  {"Also clear the selected members' personal card tweaks"}
                  <span className="block text-xs text-[var(--dojo-ink-soft)]">Photo/QR sizes, positions, QR colour, background and issued date.</span>
                </span>
              </label>

              {error && <div className="text-sm text-[var(--dojo-hinomaru)]" data-testid="apply-template-error">{error}</div>}

              {!confirming ? (
                <button type="button" disabled={!canApply} onClick={() => setConfirming(true)} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="apply-template-submit">
                  <Users className="w-4 h-4" /> Apply to {checked.size} selected
                </button>
              ) : (
                <div className="border border-[var(--dojo-hinomaru)] p-3 space-y-3" data-testid="apply-template-confirm-box">
                  <p className="text-sm">
                    Change the ID card of <strong>{checked.size} selected member{checked.size === 1 ? "" : "s"}</strong> to “{selectedTpl?.label}”
                    {clearOverrides ? " and clear their personal tweaks" : ""}? Everyone else stays as they are.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={apply} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="apply-template-confirm">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Yes, apply
                    </button>
                    <button onClick={() => setConfirming(false)} disabled={busy} className="btn-outline" data-testid="apply-template-cancel">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
